"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { UserCheck, UserPlus } from "lucide-react";
import "./DoctorProfileCard.css";

import { resolveDoctorAvatar } from "../../../../ProfileDoctor/hooks/useDoctorInformation";
import useDoctorProfile from "../../../../ProfileDoctor/hooks/useDoctorProfile";
import { useDoctorFollow } from "../../../../ProfileDoctor/hooks/useDoctorFollow";
import { DOCTOR_ROUTES } from "../../../../routes/patientRoutes/routePaths";
import {
  fetchAvailableSlotsForDay,
  fetchDoctorSlotsForDay,
} from "../../../redux/thunks/availabilityThunks";

/*======================= Mapper BD -> Profile =======================*/
const unwrapDoctor = (doc) => {
  if (!doc || typeof doc !== "object") return null;
  return (
    doc.data?.doctor ||
    doc.data?.profile ||
    doc.data?.doctorInfo ||
    doc.data ||
    doc.doctor ||
    doc.profile ||
    doc.doctorInfo ||
    doc
  );
};

const mapRegisterToProfile = (doc) => {
  const source = unwrapDoctor(doc);
  if (!source) return null;

  const sources = [
    source,
    source.user,
    source.doctor,
    source.profile,
    source.doctorInfo,
    source.data,
  ].filter((value) => value && typeof value === "object");
  const pick = (...keys) => {
    for (const candidate of sources) {
      for (const key of keys) {
        const value = candidate[key];
        if (value !== undefined && value !== null && String(value).trim()) {
          return value;
        }
      }
    }
    return "";
  };

  const safeId = String(pick("id", "_id", "doctorId", "medecinId", "userId") || "");
  const firstName = pick("firstName", "prenom", "first_name");
  const lastName = pick("lastName", "nom", "last_name");
  const localizedName = pick(
    "fullName",
    "name",
    "fullNameFr",
    "fullNameEn",
    "fullNameAr",
    "nameFr",
    "nameEn",
    "nameAr",
  );
  const locationValue = pick("location");
  const resolvedLocation = locationValue && typeof locationValue === "object"
    ? [locationValue.address, locationValue.city, locationValue.country].filter(Boolean).join(", ")
    : locationValue;

  return {
    id: safeId || null,
    image: pick("avatarUrl", "avatar", "photo", "licenceImgUrl", "identityImgUrl"),
    name: localizedName || `${firstName || ""} ${lastName || ""}`.trim(),
    specialty: pick("specialty", "speciality"),
    clinic: pick("practicePlace", "clinicAddress", "address"),
    location: resolvedLocation || [pick("city"), pick("country")].filter(Boolean).join(", "),
    nextAvailable: pick("nextAvailable") || null,
    phone: pick("phone", "phoneNumber"),
    email: pick("email"),
    bio: pick("bio", "about", "description"),
    languages: pick("languages", "spokenLanguages") || [],
    raw: source,
  };
};

/* ================= LANGUAGES RESOLUTION ================= */
const resolveLanguages = (rawDoctor, mappedDoctor, languagesFromHook) => {
  const normalize = (arr) =>
    (arr ?? [])
      .map((l) => (typeof l === "string" ? l : l?.name || l?.label || l?.language))
      .filter(Boolean);

  const fromHook = normalize(languagesFromHook);
  const fromRaw = normalize(rawDoctor?.spokenLanguages ?? rawDoctor?.languages);
  const fromMapped = normalize(mappedDoctor?.languages);

  const unique = Array.from(new Set([...fromHook, ...fromRaw, ...fromMapped]));
  return unique.length ? unique : ["Arabic", "French", "English"];
};

/* ================= EXTRACT SLOTS ================= */
const extractSlots = (result) => {
  if (!result) return [];

  let slots = [];
  if (Array.isArray(result)) slots = result;
  else if (Array.isArray(result.slots)) slots = result.slots;
  else if (result.data) {
    if (Array.isArray(result.data)) slots = result.data;
    else if (Array.isArray(result.data.slots)) slots = result.data.slots;
  }

  return slots.filter((s) => s && s.startTime && s.endTime);
};

/* ================= FIND NEXT AVAILABLE SLOT ================= */
const findNextAvailableSlot = async (medecinId, dispatch) => {
  if (!medecinId) return null;

  const today = new Date();
  const maxDaysToCheck = 90;

  for (let i = 0; i < maxDaysToCheck; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() + i);
    const dateStr = checkDate.toISOString().split("T")[0];

    try {
      let result = await dispatch(
        fetchAvailableSlotsForDay({ medecinId, date: dateStr })
      ).unwrap();

      let slots = extractSlots(result);

      if (slots.length === 0) {
        result = await dispatch(
          fetchDoctorSlotsForDay({ medecinId, date: dateStr })
        ).unwrap();

        slots = extractSlots(result).filter((slot) => slot.isAvailable !== false);
      }

      if (slots.length > 0) return dateStr;
    } catch {
      continue;
    }
  }

  return null;
};

/* ================= FORMAT DATE (i18n-aware) ================= */
const formatNextAvailableDate = (dateStr, t, i18n) => {
  if (!dateStr) return null;

  try {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() === today.getTime()) return t("doctorProfileCard.date.today");
    if (date.getTime() === tomorrow.getTime()) return t("doctorProfileCard.date.tomorrow");

    const locale = i18n?.language || "en";
    const options = { weekday: "short", month: "short", day: "numeric" };
    return date.toLocaleDateString(locale, options);
  } catch {
    return null;
  }
};

const DoctorProfileCard = ({ doctor }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mapped = useMemo(() => mapRegisterToProfile(doctor), [doctor]);
  const {
    isFollowing,
    loading: followLoading,
    toggleFollow,
    canFollow,
  } = useDoctorFollow(mapped);

  const [imgError, setImgError] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const defaultAvatar = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  const getInitials = (fullName) => {
    if (!fullName) return "?";
    const parts = fullName.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  };

  const doctorProfileId = mapped?.id;
  const { languages: languagesFromHook } = useDoctorProfile(doctorProfileId);

  const resolvedLanguages = useMemo(
    () => resolveLanguages(doctor, mapped, languagesFromHook),
    [doctor, mapped, languagesFromHook]
  );

  const resolvedImage = useMemo(() => {
    const raw = mapped?.image || "";
    return resolveDoctorAvatar(raw) || "";
  }, [mapped?.image]);

  useEffect(() => setImgError(false), [resolvedImage]);

  useEffect(() => {
    const loadNextAvailable = async () => {
      if (!doctorProfileId) return;

      if (mapped?.nextAvailable) {
        setNextAvailableDate(mapped.nextAvailable);
        return;
      }

      setLoadingAvailability(true);
      try {
        const nextDate = await findNextAvailableSlot(doctorProfileId, dispatch);
        setNextAvailableDate(nextDate);
      } catch {
        setNextAvailableDate(null);
      } finally {
        setLoadingAvailability(false);
      }
    };

    loadNextAvailable();
  }, [doctorProfileId, dispatch, mapped?.nextAvailable]);

  if (!mapped) return null;

  const { id, name, specialty, clinic, location, phone, email } = mapped;

  const showFallback = imgError || !resolvedImage;
  const initials = getInitials(name);
  const finalSrc = !imgError && resolvedImage ? resolvedImage : defaultAvatar;
  const formattedNextAvailable = formatNextAvailableDate(nextAvailableDate, t, i18n);

  const goToProfile = () => {
    if (!id) return;
    navigate(DOCTOR_ROUTES.PROFILE_DOCTOR(id));
  };

  return (
    <div className="doctor-profile-card">
      <button
        type="button"
        className={`doctor-follow-top${isFollowing ? " is-following" : ""}`}
        onClick={toggleFollow}
        disabled={followLoading || !canFollow}
        aria-label={t(
          isFollowing
            ? "doctorProfileCard.actions.unfollowDoctor"
            : "doctorProfileCard.actions.followDoctor"
        )}
        title={t(
          isFollowing
            ? "doctorProfileCard.actions.unfollowDoctor"
            : "doctorProfileCard.actions.followDoctor"
        )}
      >
        {isFollowing ? <UserCheck size={18} /> : <UserPlus size={18} />}
      </button>

      {/* PROFILE ACTION */}
      <button
        type="button"
        className="doctor-view-top"
        onClick={goToProfile}
        aria-label={t("doctorProfileCard.actions.viewProfile")}
        title={t("doctorProfileCard.actions.viewProfile")}
      >
        {t("doctorProfileCard.actions.viewProfile")}
      </button>

      {/* Header avec avatar */}
      <div className="profile-header">
        <button
          type="button"
          className="doctor-avatar-click"
          onClick={goToProfile}
          aria-label={t("doctorProfileCard.aria.openProfile", {
            name: name || t("doctorProfileCard.fallback.doctor"),
          })}
        >
          {showFallback ? (
            <div className="doctor-avatar-large doctor-avatar-fallback">
              {initials}
            </div>
          ) : (
            <img
              src={finalSrc}
              alt={name || t("doctorProfileCard.fallback.doctorAvatarAlt")}
              className="doctor-avatar-large"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                if (String(e.currentTarget.src).includes("flaticon.com/512/149/149071.png")) return;
                setImgError(true);
              }}
            />
          )}
        </button>
      </div>

      {/* Nom */}
      {name ? (
        <h2 className="doctor-name">
          <button
            type="button"
            className="doctor-name-click"
            onClick={goToProfile}
            aria-label={t("doctorProfileCard.aria.openProfile", { name })}
          >
            {t("doctorProfileCard.prefix.dr")} {name}
          </button>
        </h2>
      ) : null}

      {/* Spécialité */}
      {specialty ? <p className="doctor-specialty">{specialty}</p> : null}

      {/* Localisation */}
      {clinic || location ? (
        <p className="doctor-location">{[clinic, location].filter(Boolean).join(", ")}</p>
      ) : null}

      {/* Téléphone */}
      {phone ? (
        <p className="doctor-contact">
          <span className="contact-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <g clipPath="url(#clip0_128_18033)">
                <path d="M9.75 1.5C11.5402 1.5 13.2571 2.21116 14.523 3.47703C15.7888 4.7429 16.5 6.45979 16.5 8.25" stroke="#546881" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.75 4.5C10.7446 4.5 11.6984 4.89509 12.4017 5.59835C13.1049 6.30161 13.5 7.25544 13.5 8.25" stroke="#546881" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10.374 12.426C10.5289 12.4971 10.7034 12.5134 10.8688 12.4721C11.0341 12.4308 11.1805 12.3344 11.2838 12.1987L11.55 11.85C11.6897 11.6637 11.8709 11.5125 12.0792 11.4084C12.2875 11.3042 12.5171 11.25 12.75 11.25H15C15.3978 11.25 15.7794 11.408 16.0607 11.6893C16.342 11.9706 16.5 12.3522 16.5 12.75V15C16.5 15.3978 16.342 15.7794 16.0607 16.0607C15.7794 16.342 15.3978 16.5 15 16.5C11.4196 16.5 7.9858 15.0777 5.45406 12.5459C2.92232 10.0142 1.5 6.58042 1.5 3C1.5 2.60218 1.65804 2.22064 1.93934 1.93934C2.22064 1.65804 2.60218 1.5 3 1.5H5.25C5.64782 1.5 6.02936 1.65804 6.31066 1.93934C6.59196 2.22064 6.75 2.60218 6.75 3V5.25C6.75 5.48287 6.69578 5.71254 6.59164 5.92082C6.4875 6.1291 6.33629 6.31028 6.15 6.45L5.799 6.71325C5.66131 6.81838 5.56426 6.96794 5.52434 7.13651C5.48442 7.30509 5.50409 7.48228 5.58 7.638C6.60501 9.7199 8.29082 11.4036 10.374 12.426Z" stroke="#546881" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_128_18033">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
          <a href={`tel:${phone}`}>{phone}</a>
        </p>
      ) : null}

      {/* Email */}
      {email ? (
        <p className="doctor-contact">
          <span className="contact-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M16.5 5.25L9.75675 9.54525C9.52792 9.67816 9.268 9.74817 9.00338 9.74817C8.73875 9.74817 8.47883 9.67816 8.25 9.54525L1.5 5.25" stroke="#546881" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M15 3H3C2.17157 3 1.5 3.67157 1.5 4.5V13.5C1.5 14.3284 2.17157 15 3 15H15C15.8284 15 16.5 14.3284 16.5 13.5V4.5C16.5 3.67157 15.8284 3 15 3Z" stroke="#546881" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <a href={`mailto:${email}`}>{email}</a>
        </p>
      ) : null}

      {/* Next Available */}
      {/* <div className="doctor-next-available"> */}
        {/* <span className="next-available-label">{t("doctorProfileCard.nextAvailable.label")}</span> */}
        {/* <span className="next-available-date"> */}
          {/* {loadingAvailability 
            ? t("doctorProfileCard.nextAvailable.loading")
             : formattedNextAvailable
             ? formattedNextAvailable
             : t("doctorProfileCard.nextAvailable.none")}
        {/* </span> */}
      {/* </div> */}

      {/* Languages */}
      <div className="languages-section">
        {(resolvedLanguages || []).map((lang, idx) => (
          <button key={`${lang}-${idx}`} className="language-btn" type="button">
            {lang}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DoctorProfileCard;